/**
 * 顧客評價管理 —— 看顧客留下的評價，並代表店家回覆。
 *
 * 權限分兩級（後端同樣會擋一次）：
 *   review:manage    → 全店評價都能回
 *   review:reply_own → 只能回「指名自己」的那幾則（設計師預設）
 * 每一列的 canReply 是後端算好回傳的，前端只負責顯示。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Avatar,
  Button,
  Card,
  Empty,
  Image,
  Input,
  Rate,
  Segmented,
  Space,
  Spin,
  Tag,
  Typography,
  Pagination,
  Popconfirm,
  message
} from 'antd'
import {
  StarOutlined,
  MessageOutlined,
  ReloadOutlined,
  SearchOutlined,
  UserOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import { usePermission } from '../hooks/usePermission'
import Hint from '../components/common/Hint'
import ResponsiveSelect from '../components/common/ResponsiveSelect'
import { getApiErrorMessage } from '../utils/api'
import { PHOTO_URL } from '../utils/apiutils'
import {
  listBusinessReviews,
  replyBusinessReview,
  deleteBusinessReviewReply
} from '../api/review'
import { formatTz } from '../utils/timezone'

const { Title, Text, Paragraph } = Typography

const PAGE_SIZE = 10
const REPLY_MAX = 1000

const photoSrc = (url) =>
  !url ? '' : /^https?:\/\//.test(url) ? url : `${PHOTO_URL}${url}`

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 24px;
  background: #f5f5f5;

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const ContentWrapper = styled.div`
  max-width: 1100px;
  width: 100%;
  /* flex 子項預設 min-width:auto，內容一寬就把整頁撐開；手機版必須顯式歸零 */
  min-width: 0;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HeadCard = styled(Card)`
  .ant-card-body {
    padding: 20px 24px;
  }

  .top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }
  .top > div {
    min-width: 0;
  }

  .stats {
    display: flex;
    gap: 26px;
    flex-wrap: wrap;
  }
  .stat .n {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
    color: #d5728a;
  }
  .stat .t {
    font-size: 12px;
    color: #8a7d80;
  }

  @media (max-width: 768px) {
    .ant-card-body {
      padding: 16px;
    }
    .stats {
      gap: 20px;
      width: 100%;
    }
    .stat .n {
      font-size: 20px;
    }
  }
`

const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 16px;
  min-width: 0;

  /*
   * 手機版：Segmented 有五個選項、又不會自己換行，維持橫排一定會超出畫面寬度，
   * 把整張卡片（連同整頁）撐得比視窗寬 —— 這就是手機上看到「畫面可以左右拉」的原因。
   *
   * 解法不是把字變小，是讓它在自己的容器裡橫向捲動：
   *   1. 整排控制項改直排、拉滿寬度（手指也比較好點）
   *   2. Segmented 寬度限制在容器內，內部橫捲，捲軸隱藏
   *   3. 明確不讓選項被壓縮（flex: 0 0 auto），否則 antd 會把文字縮成 ...
   *      變成「看得到但讀不懂」，那比要捲動更糟
   * 這個做法與同人資料頁的 ConsentFormRecordsPage 一致。
   */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;

    /* 蓋掉 Select 的 width:150、Input 的 width:220 等固定寬度 */
    > * {
      width: 100% !important;
      max-width: 100%;
    }

    .ant-segmented {
      overflow-x: auto;
      overscroll-behavior-x: contain;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }
    .ant-segmented::-webkit-scrollbar {
      display: none;
    }
    .ant-segmented-group {
      flex-wrap: nowrap;
    }
    .ant-segmented-item {
      flex: 0 0 auto;
    }
    .ant-segmented-item-label {
      white-space: nowrap;
    }
  }
`

const ReviewCard = styled(Card)`
  .ant-card-body {
    padding: 18px 20px;
  }

  .top {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }
  .who {
    flex: 1 1 auto;
    min-width: 0;
  }
  .nm {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    font-weight: 600;
  }
  .meta {
    margin-top: 2px;
    font-size: 12px;
    color: #8a7d80;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .comment {
    margin: 12px 0 0;
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.8;
  }

  .shots {
    margin-top: 12px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .shots .ant-image {
    border-radius: 8px;
    overflow: hidden;
  }

  .subs {
    margin-top: 10px;
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  @media (max-width: 768px) {
    .ant-card-body {
      padding: 14px;
    }
    /* 照片縮圖在窄螢幕排兩張一行就好，第三張換行不要擠 */
    .shots .ant-image {
      max-width: calc(50% - 4px);
    }
  }
`

const ReplyBox = styled.div`
  margin-top: 14px;
  border-radius: 10px;
  background: #fdf1f5;
  padding: 12px 14px;

  .h {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #c14a70;
    margin-bottom: 6px;
    flex-wrap: wrap;
  }
  .h .when {
    font-weight: 400;
    color: #9b8b8f;
    font-size: 12px;
  }
  .h .acts {
    margin-left: auto;
    display: flex;
    gap: 4px;
  }
  p {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.8;
    color: #5a4c50;
  }
`

const Editor = styled.div`
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .foot {
    display: flex;
    align-items: center;
    gap: 8px;
    /* 送出／取消／字數三個東西在窄螢幕會擠成一排推寬容器 */
    flex-wrap: wrap;
  }
  .foot .count {
    margin-left: auto;
    font-size: 12px;
    color: #9b8b8f;
  }
`

const FILTERS = [
  { label: '全部', value: 'all' },
  { label: '未回覆', value: 'pending' },
  { label: '已回覆', value: 'replied' },
  { label: '有照片', value: 'photo' },
  { label: '低分（3 星以下）', value: 'low' }
]

const ReviewManagementPage = () => {
  const { user } = useAuth()
  const { can } = usePermission()
  const businessId = user?.businessId

  const canReplyAll = can('review:manage')
  const canReplyOwn = can('review:reply_own')

  const [rows, setRows] = useState([])
  const [summary, setSummary] = useState(null)
  const [filter, setFilter] = useState('all')
  const [sort, setSort] = useState('latest')
  const [keyword, setKeyword] = useState('')
  const [kw, setKw] = useState('')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null) // reviewId
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setKw(keyword.trim()), 350)
    return () => clearTimeout(t)
  }, [keyword])

  useEffect(() => {
    setPage(1)
  }, [filter, sort, kw])

  const params = useMemo(() => {
    const p = { page, limit: PAGE_SIZE, sort }
    if (filter === 'pending') p.hasReply = 0
    if (filter === 'replied') p.hasReply = 1
    if (filter === 'photo') p.hasPhoto = 1
    if (kw) p.keyword = kw
    return p
  }, [page, sort, filter, kw])

  const fetchRows = useCallback(async () => {
    if (!businessId) return
    setLoading(true)
    try {
      const res = await listBusinessReviews(businessId, params)
      const d = res.data?.data || {}
      let list = Array.isArray(d.reviews) ? d.reviews : []
      // 「低分」目前沒有對應的後端參數，先在前端這一頁過濾。
      // 之後要做「低分優先處理」的話再把 maxRating 加到 API。
      if (filter === 'low') list = list.filter((r) => (r.rating || 0) <= 3)
      setRows(list)
      setTotal(d.totalReviews || 0)
      setSummary(d.summary || null)
    } catch (err) {
      message.error(getApiErrorMessage(err, '載入評價失敗'))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [businessId, params, filter])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  const startEdit = (row) => {
    setEditing(row._id)
    setDraft(row.reply?.content || '')
  }

  const submitReply = async (row) => {
    const content = draft.trim()
    if (!content) {
      message.warning('請輸入回覆內容')
      return
    }
    setSaving(true)
    try {
      const res = await replyBusinessReview(businessId, row._id, content)
      const updated = res.data?.data
      setRows((prev) =>
        prev.map((r) =>
          r._id === row._id
            ? { ...r, reply: updated?.reply || { content, repliedAt: new Date() } }
            : r
        )
      )
      setSummary((s) =>
        s && !row.reply?.content
          ? { ...s, replied: (s.replied || 0) + 1, pending: Math.max(0, (s.pending || 0) - 1) }
          : s
      )
      setEditing(null)
      setDraft('')
      message.success('已送出回覆')
    } catch (err) {
      message.error(getApiErrorMessage(err, '回覆失敗'))
    } finally {
      setSaving(false)
    }
  }

  const removeReply = async (row) => {
    try {
      await deleteBusinessReviewReply(businessId, row._id)
      setRows((prev) =>
        prev.map((r) => (r._id === row._id ? { ...r, reply: null } : r))
      )
      setSummary((s) =>
        s
          ? {
              ...s,
              replied: Math.max(0, (s.replied || 0) - 1),
              pending: (s.pending || 0) + 1
            }
          : s
      )
      message.success('已刪除回覆')
    } catch (err) {
      message.error(getApiErrorMessage(err, '刪除失敗'))
    }
  }

  const mayReply = (row) =>
    row.canReply !== undefined ? row.canReply : canReplyAll

  return (
    <PageWrapper>
      <ContentWrapper>
        <HeadCard>
          <div className='top'>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                <StarOutlined style={{ color: '#d5728a', marginRight: 8 }} />
                顧客評價
              </Title>
              <Text type='secondary' style={{ fontSize: 13 }}>
                回覆會顯示在店家頁的評價區，客人和潛在客人都看得到。
              </Text>
            </div>
            <div className='stats'>
              <div className='stat'>
                <div className='n'>{summary?.total ?? '—'}</div>
                <div className='t'>總評價數</div>
              </div>
              <div className='stat'>
                <div className='n'>{summary?.average ?? '—'}</div>
                <div className='t'>平均星等</div>
              </div>
              <div className='stat'>
                <div className='n'>{summary?.pending ?? '—'}</div>
                <div className='t'>尚未回覆</div>
              </div>
            </div>
          </div>

          {!canReplyAll && canReplyOwn && (
            <div style={{ marginTop: 14 }}>
              <Hint tone='info' title='你可以回覆指名自己的評價'>
                其他設計師的評價只能檢視，回覆按鈕不會出現。需要代回覆請找店長或櫃檯。
              </Hint>
            </div>
          )}
          {!canReplyAll && !canReplyOwn && (
            <div style={{ marginTop: 14 }}>
              <Hint tone='info' title='目前為檢視模式'>
                你的角色可以查看顧客評價，但沒有回覆權限。
              </Hint>
            </div>
          )}

          <Filters>
            <Segmented
              options={FILTERS}
              value={filter}
              onChange={setFilter}
            />
            <ResponsiveSelect
              value={sort}
              onChange={setSort}
              style={{ width: 150 }}
              options={[
                { label: '最新', value: 'latest' },
                { label: '最舊', value: 'oldest' },
                { label: '評分高到低', value: 'ratingDesc' },
                { label: '評分低到高', value: 'ratingAsc' },
                { label: '最有幫助', value: 'helpful' }
              ]}
            />
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder='搜尋評價內容'
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              style={{ width: 220 }}
            />
            <Button icon={<ReloadOutlined />} onClick={fetchRows}>
              重新整理
            </Button>
          </Filters>
        </HeadCard>

        <Spin spinning={loading}>
          {rows.length === 0 && !loading ? (
            <Card>
              <Empty
                description={
                  filter === 'all' && !kw
                    ? '還沒有顧客留下評價'
                    : '找不到符合條件的評價'
                }
              />
            </Card>
          ) : (
            <Space direction='vertical' size={12} style={{ width: '100%' }}>
              {rows.map((r) => {
                const photos = Array.isArray(r.photos) ? r.photos : []
                const replied = !!r.reply?.content
                const isEditing = editing === r._id
                return (
                  <ReviewCard key={r._id}>
                    <div className='top'>
                      <Avatar
                        size={42}
                        src={r.user?.avatar ? photoSrc(r.user.avatar) : undefined}
                        icon={<UserOutlined />}
                      />
                      <div className='who'>
                        <div className='nm'>
                          {r.source === 'google'
                            ? r.authorName || 'Google 使用者'
                            : r.user?.name || '美感會員'}
                          {r.source === 'google' && <Tag color='blue'>Google</Tag>}
                          {replied ? (
                            <Tag color='green'>已回覆</Tag>
                          ) : (
                            <Tag color='orange'>未回覆</Tag>
                          )}
                        </div>
                        <div className='meta'>
                          <Rate
                            disabled
                            value={r.rating || 0}
                            style={{ fontSize: 13 }}
                          />
                          <span>
                            {formatTz(r.createdAt, 'YYYY/MM/DD', '')}
                          </span>
                          {r.serviceName && <span>・{r.serviceName}</span>}
                          {r.stylist?.name && <span>・{r.stylist.name}</span>}
                          {r.helpfulCount > 0 && (
                            <span>・{r.helpfulCount} 人覺得有幫助</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {r.comment && <Paragraph className='comment'>{r.comment}</Paragraph>}

                    {photos.length > 0 && (
                      <div className='shots'>
                        <Image.PreviewGroup>
                          {photos.map((p, i) => (
                            <Image
                              key={p || i}
                              src={photoSrc(p)}
                              alt={`評價照片 ${i + 1}`}
                              width={92}
                              height={92}
                              style={{ objectFit: 'cover' }}
                            />
                          ))}
                        </Image.PreviewGroup>
                      </div>
                    )}

                    {(r.environment || r.service || r.price) && (
                      <div className='subs'>
                        {r.environment ? <Tag>環境 {r.environment}</Tag> : null}
                        {r.service ? <Tag>服務 {r.service}</Tag> : null}
                        {r.price ? <Tag>價位 {r.price}</Tag> : null}
                      </div>
                    )}

                    {replied && !isEditing && (
                      <ReplyBox>
                        <div className='h'>
                          <MessageOutlined />
                          店家回覆
                          {r.reply.repliedAt && (
                            <span className='when'>
                              {formatTz(r.reply.repliedAt, 'YYYY/MM/DD HH:mm')}
                              {r.reply.repliedBy?.name
                                ? ` ・${r.reply.repliedBy.name}`
                                : ''}
                            </span>
                          )}
                          {mayReply(r) && (
                            <span className='acts'>
                              <Button
                                size='small'
                                type='text'
                                icon={<EditOutlined />}
                                onClick={() => startEdit(r)}
                              >
                                編輯
                              </Button>
                              <Popconfirm
                                title='要刪除這則回覆嗎？'
                                description='刪除後客人端就看不到店家回覆了。'
                                okText='刪除'
                                cancelText='取消'
                                okButtonProps={{ danger: true }}
                                onConfirm={() => removeReply(r)}
                              >
                                <Button
                                  size='small'
                                  type='text'
                                  danger
                                  icon={<DeleteOutlined />}
                                >
                                  刪除
                                </Button>
                              </Popconfirm>
                            </span>
                          )}
                        </div>
                        <p>{r.reply.content}</p>
                      </ReplyBox>
                    )}

                    {isEditing && (
                      <Editor>
                        <Input.TextArea
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          maxLength={REPLY_MAX}
                          autoSize={{ minRows: 3, maxRows: 8 }}
                          placeholder='謝謝您的回饋…（回覆會公開顯示在店家頁）'
                        />
                        <div className='foot'>
                          <Button
                            type='primary'
                            loading={saving}
                            onClick={() => submitReply(r)}
                          >
                            送出回覆
                          </Button>
                          <Button
                            onClick={() => {
                              setEditing(null)
                              setDraft('')
                            }}
                          >
                            取消
                          </Button>
                          <span className='count'>
                            {draft.length} / {REPLY_MAX}
                          </span>
                        </div>
                      </Editor>
                    )}

                    {!replied && !isEditing && mayReply(r) && (
                      <div style={{ marginTop: 14 }}>
                        <Button
                          type='primary'
                          ghost
                          icon={<MessageOutlined />}
                          onClick={() => startEdit(r)}
                        >
                          回覆這則評價
                        </Button>
                      </div>
                    )}
                  </ReviewCard>
                )
              })}

              {total > PAGE_SIZE && (
                <div style={{ textAlign: 'center', padding: '8px 0 24px' }}>
                  <Pagination
                    current={page}
                    pageSize={PAGE_SIZE}
                    total={total}
                    showSizeChanger={false}
                    onChange={setPage}
                  />
                </div>
              )}
            </Space>
          )}
        </Spin>
      </ContentWrapper>
    </PageWrapper>
  )
}

export default ReviewManagementPage
